import { CurrencyAmount, validMonthlyRate } from '../../models/payroll-currency.model';
import {
  DEAL_PAYROLL_STATUS,
  DealPayrollBreakdown,
  isDealPayrollIdentityMatch,
  isDealPayrollPeriodDiagnostic,
  isFinitePayrollNumber,
  isPlanfixDealPayroll,
  PAYROLL_CALCULATION_METHOD,
  PayrollCalculationMethodSource,
  resolvePayrollCalculationMethod
} from '../../models/deal-payroll.model';
import {
  dealPayrollSalaryPresentation,
  synchronizeDealPayrollSalary
} from '../../models/deal-payroll-report-presentation';
import {
  PLANFIX_CALCULATION_LABEL_KEY,
  PLANFIX_DEAL_CALCULATION_LABEL_KEY
} from './my-working-report.constants';
import { MyWorkingReportCurrencyMapper } from './my-working-report-currency.mapper';
import {
  PayrollCalculationPresentation,
  PayrollMonthlyRateContext,
  PayrollMonthlyRatePresentation,
  PayrollMonthlyRateSource,
  PayrollReportUser,
  ReportCurrencySummary,
  ReportViewModel
} from './my-working-report.types';

const LEGACY_MONTHLY_RATE_DIGITS = '1.2-2';
const DEAL_MONTHLY_RATE_DIGITS = '1.0-0';

export class MyWorkingReportPayrollPresentation {
  public readonly previewAmounts: {[userId: string]: CurrencyAmount[]} = {};

  private readonly currencyMapper = new MyWorkingReportCurrencyMapper();

  public mapInitialReport(report: ReportViewModel): void {
    this.applyReport(report, null);
  }

  public applyStoredReports(reports: ReportViewModel[], expectedMonth: string): void {
    reports
      .filter((report: ReportViewModel) => isPlanfixDealPayroll(report))
      .forEach((report: ReportViewModel) => this.applyReport(report, expectedMonth));
  }

  public applyBreakdown(
    report: ReportViewModel,
    breakdown: DealPayrollBreakdown,
    expectedMonth: string
  ): void {
    if (!isPlanfixDealPayroll(report)
      || !isDealPayrollIdentityMatch(report.userID, expectedMonth, breakdown)) {
      return;
    }
    report.dealPayrollBreakdown = this.compactBreakdown(breakdown);
    this.applyReport(report, expectedMonth);
  }

  private applyReport(report: ReportViewModel, expectedMonth: string | null): void {
    if (isPlanfixDealPayroll(report) && !expectedMonth
      && !isDealPayrollPeriodDiagnostic(report.userID, report.dealPayrollBreakdown)) {
      this.currencyMapper.mapCurrencyAmounts(report.summaryRecord);
      return;
    }
    const synchronized = this.synchronizeReport(report, expectedMonth);
    this.currencyMapper.mapCurrencyAmounts(report.summaryRecord);
    delete this.previewAmounts[report.userID];
    if (!isPlanfixDealPayroll(report)) {
      return;
    }
    if (!synchronized) {
      delete report.dealPayrollBreakdown;
      return;
    }
    this.applyPreview(report);
  }

  private applyPreview(report: ReportViewModel): void {
    this.clearNonPayableSalary(report);
    const presentation = dealPayrollSalaryPresentation(report);
    if (!presentation.preview) {
      return;
    }
    const previewSummary: ReportCurrencySummary = Object.assign(
      {}, report.summaryRecord, {salaryAmounts: presentation.amounts});
    this.currencyMapper.mapCurrencyAmounts(previewSummary);
    this.previewAmounts[report.userID] = previewSummary.orderedSalaryAmounts;
  }

  private synchronizeReport(report: ReportViewModel, expectedMonth: string | null): boolean {
    return !!(isPlanfixDealPayroll(report) && report.dealPayrollBreakdown
      && synchronizeDealPayrollSalary(report, report.dealPayrollBreakdown, expectedMonth));
  }

  private clearNonPayableSalary(report: ReportViewModel): void {
    if (report.dealPayrollBreakdown
      && report.dealPayrollBreakdown.status !== DEAL_PAYROLL_STATUS.COMPLETE) {
      report.summaryRecord.orderedSalaryAmounts = [];
    }
  }

  private compactBreakdown(breakdown: DealPayrollBreakdown): DealPayrollBreakdown {
    const compact = Object.assign({}, breakdown);
    delete compact.taskLines;
    return compact;
  }
}

export function payrollCalculationPresentation(
  user: PayrollCalculationMethodSource
): PayrollCalculationPresentation {
  const method = resolvePayrollCalculationMethod(user);
  return {
    internal: method === PAYROLL_CALCULATION_METHOD.INTERNAL,
    labelKey: method === PAYROLL_CALCULATION_METHOD.PLANFIX_DEAL
      ? PLANFIX_DEAL_CALCULATION_LABEL_KEY : PLANFIX_CALCULATION_LABEL_KEY
  };
}

export function payrollMonthlyRate(
  user: PayrollReportUser,
  report: PayrollMonthlyRateSource,
  context: PayrollMonthlyRateContext
): PayrollMonthlyRatePresentation | null {
  const rate = report && report.monthlyRate;
  const deal = isPlanfixDealPayroll(report) || isPlanfixDealPayroll(user);
  if (context.userId && (!report || report.userID !== context.userId)) { return {amount: null, currency: null}; }
  if (deal && (!report || !isDealPayrollIdentityMatch(report.userID, context.month, report.dealPayrollBreakdown))) {
    return {amount: null, currency: null};
  }
  if (rate) {
    if (!validMonthlyRate(rate) || rate.month !== context.month) { return {amount: null, currency: null}; }
    return {amount: rate.effectiveAmount, baseAmount: rate.baseAmount, bonusAmount: rate.bonusAmount,
      currency: rate.currency, digitsInfo: deal ? DEAL_MONTHLY_RATE_DIGITS : LEGACY_MONTHLY_RATE_DIGITS};
  }
  return legacyDealMonthlyRate(report, context);
}

function legacyDealMonthlyRate(report: PayrollMonthlyRateSource, context: PayrollMonthlyRateContext): PayrollMonthlyRatePresentation {
  const breakdown = report && report.dealPayrollBreakdown;
  if (!report || !isDealPayrollIdentityMatch(report.userID, context.month, breakdown)
    || !isFinitePayrollNumber(breakdown.effectiveMonthlySalary)) { return {amount: null, currency: null}; }
  const amount = breakdown.effectiveMonthlySalary;
  return isFinitePayrollNumber(amount)
    ? {amount: amount, currency: breakdown.currency} : {amount: null, currency: null};
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-working-report/my-working-report-payroll.presentation.ts